"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Calendar, ArrowRight, Clock, Check, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 90);
    const interval = setInterval(() => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      if (difference <= 0) {
        clearInterval(interval);
        return;
      }
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(difference / (1000 * 60 * 60) % 24);
      const minutes = Math.floor(difference / 1000 / 60 % 60);
      const seconds = Math.floor(difference / 1000 % 60);
      setTimeLeft({
        days,
        hours,
        minutes,
        seconds
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const TimeBlock = ({
    value,
    label
  }: {
    value: number;
    label: string;
  }) => <div className="flex flex-col items-center">
      <div className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-primary tabular-nums leading-none">
        {value.toString().padStart(2, "0")}
      </div>
      <div className="text-xs md:text-sm uppercase tracking-widest text-muted-foreground mt-2">
        {label}
      </div>
    </div>;
  return <div className="grid grid-cols-4 gap-4 md:gap-8 w-full max-w-lg mx-auto lg:mx-0">
      <TimeBlock value={timeLeft.days} label="Days" />
      <TimeBlock value={timeLeft.hours} label="Hrs" />
      <TimeBlock value={timeLeft.minutes} label="Mins" />
      <TimeBlock value={timeLeft.seconds} label="Secs" />
    </div>;
}
function SponsorshipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in duration-500">
        <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center text-secondary mb-4">
          <Check className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-primary">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 uppercase tracking-wider text-xs">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="border-0 border-b border-primary/40 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/30 h-10" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 uppercase tracking-wider text-xs">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-primary/40 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/30 h-10" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 uppercase tracking-wider text-xs">
                Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} className="border-0 border-b border-primary/40 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/30 h-10" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 uppercase tracking-wider text-xs">
                Your Role
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className="w-full border-0 border-b border-primary/40 rounded-none px-0 bg-transparent focus:ring-0 focus:border-primary h-10 text-sm text-foreground appearance-none cursor-pointer">
                    <option value="" disabled className="bg-card text-muted-foreground">
                      Select your role
                    </option>
                    {["CEO / Founder", "Entrepreneur", "Government Official", "Cultural Leader", "Media / Press", "Other"].map(option => <option key={option} value={option} className="bg-card text-foreground">
                        {option}
                      </option>)}
                  </select>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-primary/50">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/80 uppercase tracking-wider text-xs block mb-4">
              I'm Interested In
            </FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Attending the Event", "Becoming a Sponsor", "Volunteering", "Media Coverage", "Receiving Newsletter"].map(option => {
          const isChecked = (field.value || []).includes(option);
          return <label key={option} className={`flex items-center space-x-3 p-3 border rounded-sm cursor-pointer transition-all duration-200 ${isChecked ? "border-primary bg-primary/10" : "border-primary/20 hover:border-primary/40 bg-transparent"}`}>
                    <input type="checkbox" checked={isChecked} onChange={e => {
              const checked = e.target.checked;
              const currentValues = field.value || [];
              if (checked) {
                field.onChange([...currentValues, option]);
              } else {
                field.onChange(currentValues.filter((v: string) => v !== option));
              }
            }} className="appearance-none w-4 h-4 border border-primary/50 rounded-sm bg-transparent checked:bg-primary checked:border-primary focus:ring-1 focus:ring-primary/50 transition-colors relative flex items-center justify-center after:content-[''] after:text-black after:text-[10px] after:font-bold after:hidden checked:after:block" />
                    <span className="text-sm text-foreground/90">{option}</span>
                  </label>;
        })}
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-secondary hover:bg-secondary/90 text-white font-medium tracking-wide px-8 py-6 h-auto text-lg rounded-sm shadow-lg shadow-secondary/20 transition-all duration-300 hover:shadow-secondary/40">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 h-5 w-5" />
              Processing...
            </> : <>
              Submit Inquiry
              <ArrowRight className="ml-2 h-5 w-5" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function JoinTheMovement() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="join-the-movement" ref={sectionRef} className="dark bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/botanic_gardens_map_dark.png)] bg-cover bg-center opacity-20 mix-blend-overlay pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background opacity-80 pointer-events-none" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen lg:min-h-[800px]">
          
          {}
          <div className={`flex flex-col justify-center py-16 lg:py-24 lg:pr-16 border-b lg:border-b-0 lg:border-r border-primary/20 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="space-y-8 max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium tracking-widest uppercase">
                <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate>
                <span>Save the Date</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground leading-tight">
                <span className="text-primary block mb-2">TICKETS</span>
                ON SALE SOON
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-md mx-auto lg:mx-0">
                Secure your place at Colorado's premier multicultural culinary and corporate experience. Don't miss the event of the year.
              </p>

              <div className="py-8">
                <CountdownTimer />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-8 py-6 h-auto rounded-sm shadow-lg shadow-primary/20">
                  <Link to="#tickets">
                    Buy Tickets Now
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-primary/30 text-primary hover:bg-primary/10 hover:text-primary text-lg px-8 py-6 h-auto rounded-sm">
                  <Link to="#experience">
                    View Experiences
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className={`flex flex-col justify-center py-16 lg:py-24 lg:pl-16 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="max-w-xl mx-auto lg:mx-0 w-full">
              <div className="mb-10 text-center lg:text-left">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
                  <span className="text-secondary">SPONSORSHIP</span>
                </h2>
                <p className="text-muted-foreground">
                  Partner with SABOR DENVER to connect with influential leaders and elevate your brand.
                </p>
              </div>

              <div className="bg-card/30 backdrop-blur-sm border border-white/5 p-6 md:p-8 rounded-xl shadow-2xl shadow-black/50">
                <FormProvider formId="join_movement_form_home" sectionName="join-the-movement" defaultValues={{
                full_name: "",
                email: "",
                organization: "",
                role: "",
                interest: []
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                }
              }} submitText="Submit Inquiry" successMessage="Thank you for your interest in SABOR DENVER. Our team will contact you shortly to discuss sponsorship opportunities.">
                  <SponsorshipForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}